import ObjectID from 'bson-objectid';
import { Suspense } from 'react';
import { Navigate, RouteObject, useParams } from 'react-router-dom';
import Authenticated from 'src/components/Authenticated';
import Guest from 'src/components/Guest';
import SuspenseLoader from 'src/components/SuspenseLoader';

import AccentSidebarLayout from '../layouts/AccentSidebarLayout';
import { lazyWithRetry } from '../lazyWithRetry';

// eslint-disable-next-line react/display-name
const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

const Records = Loader(
  lazyWithRetry(() => import('src/content/management/Records'))
);
const SingleRecord = Loader(
  lazyWithRetry(() => import('src/content/management/Records/single'))
);
const ControlCenter = Loader(
  lazyWithRetry(() => import('src/content/management/ControlCenter'))
);

// Hidden support routes retained for client creation and model publication.
const Workspaces = Loader(
  lazyWithRetry(() => import('src/content/management/Workspaces'))
);
const SingleWorkspace = Loader(
  lazyWithRetry(() => import('src/content/management/Workspaces/single'))
);
const CreateClient = Loader(
  lazyWithRetry(() => import('src/content/management/Workspaces/CreateClient'))
);
const Users = Loader(
  lazyWithRetry(() => import('src/content/management/Users'))
);
const MlConfigurations = Loader(
  lazyWithRetry(() => import('src/content/management/MlConfigurations'))
);
const MlDeployedModels = Loader(
  lazyWithRetry(() => import('src/content/management/MlDeployedModels'))
);

const LoginBasic = Loader(
  lazyWithRetry(() => import('src/content/pages/Auth/Login/Basic'))
);
const RegisterBasic = Loader(
  lazyWithRetry(() => import('src/content/pages/Auth/Register/Basic'))
);
const RecoverPassword = Loader(
  lazyWithRetry(() => import('src/content/pages/Auth/RecoverPassword'))
);
const SetNewPassword = Loader(
  lazyWithRetry(() => import('src/content/pages/Auth/SetNewPassword'))
);
const Status404 = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/Maintenance'))
);

const SingleRecordRoute = () => {
  const { recordId } = useParams();

  return recordId && ObjectID.isValid(recordId) ? (
    <SingleRecord />
  ) : (
    <Status404 />
  );
};

const authenticatedLayout = (
  <Authenticated>
    <AccentSidebarLayout />
  </Authenticated>
);

const routes: RouteObject[] = [
  {
    path: 'account',
    children: [
      {
        path: 'login',
        element: (
          <Guest>
            <LoginBasic />
          </Guest>
        )
      },
      {
        path: 'organisation/register/:organisationId',
        element: (
          <Guest>
            <RegisterBasic />
          </Guest>
        )
      },
      {
        path: 'register',
        element: (
          <Guest>
            <RegisterBasic />
          </Guest>
        ),
        children: [
          {
            path: ':workspaceId',
            element: (
              <Guest>
                <RegisterBasic />
              </Guest>
            )
          }
        ]
      },
      {
        path: 'recover-password',
        element: <RecoverPassword />
      },
      {
        path: 'set-new-password/:userId/:passwordToken',
        element: <SetNewPassword />
      }
    ]
  },
  {
    path: 'activity',
    element: authenticatedLayout,
    children: [
      {
        path: '',
        element: <Navigate to="/activity/list" replace />
      },
      {
        path: 'list',
        element: <Records />
      },
      {
        path: ':recordId',
        element: <SingleRecordRoute />
      }
    ]
  },
  {
    path: 'settings',
    element: authenticatedLayout,
    children: [
      {
        path: 'control-center',
        element: <Navigate to="/settings/control-center/location" replace />
      },
      {
        path: 'control-center/*',
        element: <ControlCenter />
      }
    ]
  },
  {
    path: 'support',
    element: authenticatedLayout,
    children: [
      {
        path: '',
        element: <Navigate to="/support/workspaces/list" replace />
      },
      {
        path: 'workspaces',
        children: [
          {
            path: '',
            element: <Navigate to="/support/workspaces/list" replace />
          },
          {
            path: 'list',
            element: <Workspaces />
          },
          {
            path: 'create',
            element: <SingleWorkspace />
          },
          {
            path: 'create-client',
            element: <CreateClient />
          },
          {
            path: ':workspaceId',
            element: <SingleWorkspace />
          }
        ]
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            element: <Navigate to="/support/users/list" replace />
          },
          {
            path: 'list',
            element: <Users />
          }
        ]
      },
      {
        path: 'mlconfigurations/list',
        element: <MlConfigurations />
      },
      {
        path: 'mldeployedmodels/list',
        element: <MlDeployedModels />
      }
    ]
  },
  {
    path: '*',
    element: authenticatedLayout,
    children: [
      {
        path: '',
        element: <Navigate to="/activity/list" replace />
      },
      {
        path: 'status/404',
        element: <Status404 />
      },
      {
        path: 'status/500',
        element: <Status500 />
      },
      {
        path: 'status/maintenance',
        element: <StatusMaintenance />
      },
      {
        path: 'status/coming-soon',
        element: <StatusComingSoon />
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  }
];

export default routes;
